import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Tag,
  Plus,
  Search,
  RefreshCw,
  Edit,
  Eye,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getAdminCoupons,
  createCoupon,
  updateCoupon,
  deactivateCoupon,
  getCouponUsage,
} from "../../config/api";

const TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Europe/London",
  "America/New_York",
  "UTC",
];

const emptyForm = () => ({
  code: "",
  description: "",
  discount_type: "percent",
  discount_value: "",
  max_uses: "",
  min_order_amount: "0",
  valid_from: "",
  valid_until: "",
  timezone: "Asia/Kolkata",
  is_active: true,
});

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [usageCoupon, setUsageCoupon] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await getAdminCoupons({
        search: searchTerm || undefined,
        limit: 50,
      });
      setCoupons(res.data || []);
    } catch {
      toast.error("Failed to load coupons");
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description || "",
      discount_type: coupon.discount_type,
      discount_value: String(coupon.discount_value),
      max_uses: coupon.max_uses != null ? String(coupon.max_uses) : "",
      min_order_amount: String(coupon.min_order_amount ?? 0),
      valid_from: coupon.valid_from?.slice(0, 16) || "",
      valid_until: coupon.valid_until?.slice(0, 16) || "",
      timezone: coupon.timezone || "Asia/Kolkata",
      is_active: coupon.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim() || null,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        min_order_amount: Number(form.min_order_amount) || 0,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        timezone: form.timezone,
        is_active: form.is_active,
      };

      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, payload);
        toast.success("Coupon updated");
      } else {
        await createCoupon(payload);
        toast.success("Coupon created");
      }
      setShowModal(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save coupon");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (coupon) => {
    if (!window.confirm(`Deactivate coupon ${coupon.code}?`)) return;
    try {
      await deactivateCoupon(coupon.id);
      toast.success("Coupon deactivated");
      fetchCoupons();
    } catch {
      toast.error("Failed to deactivate coupon");
    }
  };

  const viewUsage = async (coupon) => {
    setUsageCoupon(coupon);
    setUsageLoading(true);
    try {
      const res = await getCouponUsage(coupon.id);
      setUsageRows(res.data || []);
    } catch {
      toast.error("Failed to load usage");
      setUsageRows([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const formatDiscount = (coupon) =>
    coupon.discount_type === "percent"
      ? `${coupon.discount_value}%`
      : `₹${Number(coupon.discount_value).toFixed(2)}`;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="text-primary-600" />
            Coupon Management
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Create discount codes with usage limits and validity windows
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Coupon
        </button>
      </div>

      <div className="bg-white border p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300"
          />
        </div>
        <button
          onClick={fetchCoupons}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white border overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading coupons...</div>
        ) : coupons.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No coupons yet</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Code</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Discount</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Uses</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Valid</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-semibold">{coupon.code}</td>
                  <td className="px-4 py-3">{formatDiscount(coupon)}</td>
                  <td className="px-4 py-3">
                    {coupon.used_count}
                    {coupon.max_uses != null ? ` / ${coupon.max_uses}` : " / ∞"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    <div>{coupon.valid_from_display}</div>
                    <div>→ {coupon.valid_until_display}</div>
                    <div className="text-gray-400">{coupon.timezone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        coupon.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {coupon.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => viewUsage(coupon)}
                      className="text-gray-600 hover:text-gray-900"
                      title="View usage"
                    >
                      <Eye className="w-4 h-4 inline" />
                    </button>
                    <button
                      onClick={() => openEdit(coupon)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="w-4 h-4 inline" />
                    </button>
                    {coupon.is_active && (
                      <button
                        onClick={() => handleDeactivate(coupon)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XCircle className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
          >
            <h2 className="text-lg font-bold mb-4">
              {editingCoupon ? "Edit Coupon" : "Create Coupon"}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  required
                  value={form.code}
                  onChange={(e) =>
                    setForm({ ...form, code: e.target.value.toUpperCase() })
                  }
                  className="w-full border px-3 py-2 font-mono uppercase"
                  placeholder="SAVE10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border px-3 py-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={form.discount_type}
                    onChange={(e) =>
                      setForm({ ...form, discount_type: e.target.value })
                    }
                    className="w-full border px-3 py-2"
                  >
                    <option value="percent">Percent %</option>
                    <option value="fixed">Fixed ₹</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Value</label>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.discount_value}
                    onChange={(e) =>
                      setForm({ ...form, discount_value: e.target.value })
                    }
                    className="w-full border px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Max uses</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    className="w-full border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min order ₹</label>
                  <input
                    type="number"
                    min="0"
                    value={form.min_order_amount}
                    onChange={(e) =>
                      setForm({ ...form, min_order_amount: e.target.value })
                    }
                    className="w-full border px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Timezone</label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="w-full border px-3 py-2"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Valid from</label>
                  <input
                    required
                    type="datetime-local"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                    className="w-full border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valid until</label>
                  <input
                    required
                    type="datetime-local"
                    value={form.valid_until}
                    onChange={(e) =>
                      setForm({ ...form, valid_until: e.target.value })
                    }
                    className="w-full border px-3 py-2"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
                Active
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-black text-white disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {usageCoupon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                Usage: {usageCoupon.code}
              </h2>
              <button onClick={() => setUsageCoupon(null)}>✕</button>
            </div>
            {usageLoading ? (
              <p className="text-gray-500">Loading...</p>
            ) : usageRows.length === 0 ? (
              <p className="text-gray-500">No redemptions yet</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Order</th>
                    <th className="text-left py-2">Discount</th>
                    <th className="text-left py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usageRows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="py-2">
                        {row.orders?.order_number || row.order_id}
                      </td>
                      <td className="py-2">₹{Number(row.discount_amount).toFixed(2)}</td>
                      <td className="py-2 text-gray-600">
                        {new Date(row.used_at).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
